import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import VerificationInput from 'react-verification-input';
import styled from 'styled-components';

import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Text } from '../../../components/Text';
import { authTestIDs } from '../../../constants/test/auth.testIds';
import { ForgotPasswordTokenDto } from '../../../model/user/dto/ForgotPasswordTokenDto';
import { AtiraLinkActions } from '../../../redux/atira-link/atira-link.slice';
import { useAppDispatch } from '../../../redux/store';
import { userActions } from '../../../redux/user/user.slice';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';
import { onNavigateWithRedirect } from '../../../utils/OnNavigateWithRedirect';
import { onSetTypedCRUDErrors } from '../../../utils/onSetTypedCRUDErrors';

const InputWrapper = styled(Flex)<{ valid: boolean }>`
  flex-direction: column;
  gap: ${Spacing.s};
  width: 100%;
  margin-top: ${Spacing.xl};

  .vi__container {
    width: 100% !important;
    direction: ltr;
  }

  .vi__character {
    border: ${({ theme, valid }) =>
      `2px solid ${valid ? `${theme.fgSecondary}30` : theme.danger}`} !important;
    background-color: ${({ theme }) => theme.transparent} !important;
    border-radius: ${Rounded.lg} !important;
  }
`;

const StyledButton = styled(Button)`
  height: 3.5rem;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;
  margin-top: ${Spacing.m};

  &:hover {
    background-color: ${({ theme }) => theme.mainHover};
  }
`;

export const SignupToken: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, getValues, setError } =
    useFormContext<ForgotPasswordTokenDto>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onNext = async () => {
    try {
      setLoading(true);
      const token = getValues('token');

      const { user } = await dispatch(
        userActions.verifyUserAccount({ token }),
      ).unwrap();
      await dispatch(
        AtiraLinkActions.getUserAtiraLinks({ userId: user?._id }),
      ).unwrap();

      onNavigateWithRedirect(navigate);
      AtiraToast.success(t('welcome_message'));
    } catch (e: any) {
      AtiraToast.apiError(e, { autoClose: 6000 });
      console.log(e);

      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      width={'100%'}
      gap="s"
    >
      <Text>{t('signup.verification.token')}</Text>
      <Controller
        name="token"
        control={control}
        rules={{ required: true, minLength: 6 }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <InputWrapper valid={!error}>
            <VerificationInput
              placeholder={''}
              onChange={onChange}
              value={value}
            />

            {error?.message ? (
              <Text fontSize="s" color="danger">
                {error.message}
              </Text>
            ) : null}
          </InputWrapper>
        )}
      />
      <StyledButton
        onClick={handleSubmit(onNext)}
        loading={loading}
        dataTestId={authTestIDs.signup.code.submit}
      >
        {t('signup.verification.button')}
      </StyledButton>
    </Flex>
  );
};
